import type { ExamStatus } from '@/models/UserTypes';

/**
 * Bridges the /try "when are you testing?" answer across the trial-signup flow.
 *
 * The /try diagnostic appends `?examStatus=` to the trial CTA, but the signup
 * flow calls `navigate('/trial-setup')` which drops all query params before
 * Onboarding renders — so the URL alone can't carry the status through. We
 * stash it in sessionStorage at trial-signup and read-and-clear it in
 * Onboarding to pre-fill the scheduler. sessionStorage (not localStorage) so
 * it dies with the tab and never leaks into a later session. Mirrors the
 * `pendingSocialSignup` bridge used for the redirect-auth round-trip.
 */
const KEY = 'swtpa_pending_exam_status';

const VALID: ExamStatus[] = ['scheduled', 'approved', 'waiting', 'accruing', 'exploring'];

const isValid = (v: unknown): v is ExamStatus =>
  typeof v === 'string' && (VALID as string[]).includes(v);

export function setPendingExamStatus(status: string | null | undefined): void {
  if (!isValid(status)) return; // ignore absent/garbage values
  try {
    sessionStorage.setItem(KEY, status);
  } catch {
    /* sessionStorage unavailable (private mode / blocked) — best effort */
  }
}

/** Reads the pending status AND clears it — one-shot handoff. */
export function takePendingExamStatus(): ExamStatus | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw) sessionStorage.removeItem(KEY);
    return isValid(raw) ? raw : null;
  } catch {
    return null;
  }
}
